import { Component, HostListener, OnDestroy, ViewChild } from "@angular/core";
import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { AlertController, LoadingController, ModalController, ToastController } from "@ionic/angular";
import { debounceTime, distinctUntilChanged, pairwise, startWith } from "rxjs/operators";
import { BaseClass } from "src/app/shared/classes/base";
import { AutoSaveComponent } from "src/app/shared/components/autosave/autosave.component";
import { ProcessConfig, QuestionConfig, QuestionConfigTypeEnum, QuestionGroup, QuestionTypeEnum } from "src/app/shared/interfaces/process-config.interface";
import { AttributeType } from "src/app/shared/modals/filters/models/filter-models";
import { QuestionGroupAttributesPipe } from "src/app/shared/pipes/question-group-attributes.pipe";
import { AuthService } from "src/app/shared/services/auth.service";
import { TranslationService } from "src/app/shared/services/translation.service";
import { UtilService } from "src/app/shared/services/util.service";
import { environment } from "src/environments/environment";
import { ReviewEvaluationsModal } from "../../shared/components/review-evaluations/review-evaluations.modal";
import { ParticipantOverview } from "../../calibration/_interfaces/participant-overview.interface";
import { AiGeneralSummaryModalComponent } from "../../shared/modals/ai-general-summary-modal/ai-general-summary-modal.component";
import { AiManagementEvaluationComponent } from "../../shared/modals/ai-management-evaluation/ai-management-evaluation.component";
import { FinalEvaluationStatus, ManagedUser } from "../_interfaces/managed-user.interface";
import { ManagerEvaluationRecord } from "../_interfaces/manager-evaluation-record";
import { ManagerEvaluationService } from "../manager-evaluation.service";
import { QuestionAttributePipe } from "src/app/shared/pipes/question-attribute.pipe";
import { PreviousManagerEvaluationsModal } from "./previous-manager-evaluations/previous-manager-evaluations.modal";
import { AxiConfiguration } from "src/app/calibration/components/calibration-nine-box/models/axi.model";
import { ConfigService } from "src/app/shared/services/multi-clients/config.service";


@Component({
    selector: "app-evaluate-team",
    templateUrl: "./evaluate-team.page.html",
    styleUrls: ["./evaluate-team.page.scss"],
})
export class EvaluateTeamPage extends BaseClass implements OnDestroy {
    @ViewChild("autosave", { static: true }) autosave: AutoSaveComponent;
    public hideSeeEvaluations: boolean;
    public questionsType: any;
    public hideTwoPlusTwoButton: boolean;
    public hideQuestionNumber: boolean;
    public aiReviewEnabled: boolean;
    public showNineboxPreview: boolean;
    public hasAiReviewRole: boolean = false;
    public canSeePreviousEvaluations: boolean = false;

    public processConfig: ProcessConfig;
    public allQuestions: Array<QuestionConfig> = [];
    public status: FinalEvaluationStatus;
    public formGroup: UntypedFormGroup;
    public user: ManagedUser;
    public FinalEvaluationStatus = FinalEvaluationStatus;
    public goalsIndexes: Array<{ id: number; index: number }> = [];
    public calibration = false;
    public isBusinessPartner = false;
    public isSaving = false;
    public allowAutoSave: boolean = true;
    public timerStart: boolean = false;
    public autoSaving: boolean = false;
    public hasPendingChanges: boolean = false;
    public hasTwoPlusTwoCoaching: boolean = false;
    public timerId;
    public meetingClosed: boolean = false;
    public hasAiReviewAttribute: boolean = false;
    public nineboxConfig: AxiConfiguration;
    public nineboxXOrder: number;
    public nineboxYOrder: number;

    private readonly _cycleId: number;
    private readonly _userId: string;
    private _returnPath: string = undefined;
    private overviewUser: Partial<ParticipantOverview>;

    constructor(
        protected _toastController: ToastController,
        protected _translationService: TranslationService,
        private _modalController: ModalController,
        private _alertController: AlertController,
        protected _loadingController: LoadingController,
        private _router: Router,
        private _activatedRoute: ActivatedRoute,
        private _evaluationService: ManagerEvaluationService,
        private _utilService: UtilService,
        private _authService: AuthService,
        private _questionGroupsAttributePipe: QuestionGroupAttributesPipe,
        private _questionAttributePipe: QuestionAttributePipe,
        private _configService: ConfigService, // <-- Adicionado
    ) {
        super(_toastController, _translationService);
        this._cycleId = parseInt(this._activatedRoute.snapshot.paramMap.get("cycleId"), 10);
        this._userId = this._activatedRoute.snapshot.paramMap.get("userId");
        if (this._activatedRoute.snapshot.paramMap.get("calibration")) this.calibration = true;
        this.isBusinessPartner = _authService.isInRole("BusinessPartner");
        this.hasAiReviewRole = _authService.isInRole("AI-Review");

        this.hideSeeEvaluations = this._configService.settings.features.hideSeeEvaluationsOnManagerEvalButton;
        this.questionsType = this._configService.settings.features.questionTypeAvailability.question;
        this.hideTwoPlusTwoButton = this._configService.settings.features.hideTwoPlusTwoButton;
        this.hideQuestionNumber = this._configService.settings.features.hideQuestionNumber;
        this.aiReviewEnabled = this._configService.settings.features.aiReview;
        this.showNineboxPreview = this._configService.settings.features.showNineboxPreview;
    }

    async ionViewWillEnter() {
        await this._loadEvaluation();
        this._returnPath = localStorage.getItem("UserOverview_ReturnPath");
        if (this.aiReviewEnabled) {
            this._hasAiReviewAccess();
        }
    }

    public goBack(): void {
        this._router.navigate([this._returnPath ? this._returnPath : "manager-evaluation/" + this._cycleId]);
    }

    public async view360Review(): Promise<void> {
        const modal = await this._modalController.create({
            component: ReviewEvaluationsModal,
            cssClass: "new-goals-input round bigger-modal",
            componentProps: {
                cycleId: this._cycleId,
                userId: this._userId,
            },
        });

        return await modal.present();
    }

    public goToNotepad(): void {
        this._setReturnPath("Notepad_ReturnPath");
        this._router.navigate(["notepad"]);
    }

    public goToCoaching(): void {
        this._setReturnPath("GoalsReview_ReturnPath");
        this._router.navigate(["goals-review-validation/" + this._cycleId + "/" + this._userId]);
    }

    public async save(formGroup: UntypedFormGroup, showSuccessNotify: boolean = true): Promise<void> {
        if (this.meetingClosed) {
            const alert = await this._alertController.create({
                message: this._translationService.getTranslation("calibration.confirmEditGrade"),
                buttons: [
                    {
                        text: this._translationService.getTranslation("generic.cancel"),
                        role: "cancel",
                        cssClass: "danger",
                    },
                    {
                        text: this._translationService.getTranslation("generic.editAnyway"),
                        handler: () => {
                            this._saveEvaluation(formGroup, showSuccessNotify);
                        },
                    },
                ],
            });

            await alert.present();
        } else {
            this._saveEvaluation(formGroup, showSuccessNotify);
        }
    }

    public viewGeneralSummary() {
        this._evaluationService.getUserSummary(this.user.selfEvaluation ? this.user.selfEvaluation.cycleParticipantId : null, this._cycleId, this.selectedLanguage, true).subscribe(
            async (response) => {
                let userOverviews = response.data;
                userOverviews.forEach((user) => {
                    if (user.questionsGroupsCalibrationData) {
                        user.questionsGroupsCalibrationData.forEach((group) => {
                            group.questions = this._utilService.groupQuestions(group.questions);
                        });
                    }

                    if (user.threeSixtyEvaluationCalibrationData) {
                        user.threeSixtyEvaluationCalibrationData.forEach((group) => {
                            group.questions = this._utilService.groupQuestions(group.questions);
                        });
                    }
                });
                this.overviewUser = userOverviews[0];

                const modal = await this._modalController.create({
                    component: AiGeneralSummaryModalComponent,
                    componentProps: {
                        participant: this.overviewUser,
                        cycleParticipantId: this.user.selfEvaluation ? this.user.selfEvaluation.cycleParticipantId : null,
                        cycleId: this._cycleId,
                        managerEvaluationView: true,
                    },
                    cssClass: "ai-management-evaluation-detailed round",
                });
                await modal.present();
            },
            (error) => {
                this.notifyError(error);
            },
        );
    }

    private _saveEvaluation(formGroup: UntypedFormGroup, showSuccessNotify = true): void {
        this.autoSaving = true;
        this.timerStart = false;
        clearTimeout(this.timerId);

        const evaluation = this._utilService.adjustRatingsForGoals(this.processConfig.questionsGroups, formGroup.getRawValue()) as ManagerEvaluationRecord;

        const goalGroup = this.processConfig.questionsGroups.find((qg) => qg.questions.some((q) => q.type === QuestionConfigTypeEnum.GoalsFinalGrade));

        if (goalGroup && goalGroup.questions.length == 1) {
            for (let i = 11; i >= goalGroup.questions[0].order; i--) {
                evaluation["field" + (i + 1)] = evaluation["field" + i];
                if (i == goalGroup.questions[0].order) evaluation["field" + i] = null;
            }
        }

        evaluation.calibration = this.calibration;
        this.isSaving = true;
        this._evaluationService.saveManagerEvaluation(evaluation).subscribe(
            () => {
                if (showSuccessNotify) {
                    this.hasPendingChanges = false;
                    this.notifySuccess();
                }

                this.autoSaving = false;
            },
            (error) => {
                this.notifyError(error);
                this.autoSaving = false;
            },
            () => (this.isSaving = false),
        );
    }

    public send(formGroup: UntypedFormGroup): void {
        formGroup.updateValueAndValidity();

        if (!formGroup.valid) {
            formGroup["missingFields"] = true;
            this.notify(this._translationService.getTranslation("generic.requiredValueNotProvided"), "danger");
            return;
        }
        this._confirmSendEvaluation(formGroup);
    }

    private async _confirmSendEvaluation(formGroup: UntypedFormGroup): Promise<void> {
        const alert = await this._alertController.create({
            message: this._translationService.getTranslation("managerEvaluation.confirmSend"),
            buttons: [
                {
                    text: this._translationService.getTranslation("generic.no"),
                    role: "cancel",
                    cssClass: "danger",
                },
                {
                    text: this._translationService.getTranslation("generic.yes"),
                    handler: async () => {
                        const loading = await this._loadingController.create();
                        await loading.present();

                        const evaluation = this._utilService.adjustRatingsForGoals(this.processConfig.questionsGroups, formGroup.getRawValue()) as ManagerEvaluationRecord;

                        this.isSaving = true;

                        var legalEntitySubordinateAttribute = this.user.attributes.filter((a) => a.id === 2551 && a.type === AttributeType.LegalEntity);

                        if (legalEntitySubordinateAttribute.length > 0 && this.user.lineManager2 && this.user.lineManager === this.getAtualUserId()) {
                            this.notify(this._translationService.getTranslation("managerEvaluation.notInManagerEvaluationRule"), "danger");
                            loading.dismiss();
                            this.isSaving = false;
                            return;
                        }

                        this._evaluationService.sendManagerEvaluation(evaluation).subscribe(
                            () => {
                                loading.dismiss();
                                this.notifySuccess();
                                this.goBack();
                            },
                            (error) => {
                                loading.dismiss();
                                this.notifyError(error);
                                this.isSaving = false;
                            },
                            () => (this.isSaving = false),
                        );
                    },
                },
            ],
        });

        await alert.present();
    }

    private async _loadEvaluation(): Promise<void> {
        const loading = await this._loadingController.create();
        await loading.present();

        this._evaluationService.getTeamUserForEvaluation(this._cycleId, this._userId).subscribe(
            async (response) => {
                this.processConfig = response.data.config;
                if (this.processConfig.questions) {
                    this.allQuestions = [...this.allQuestions, ...this.processConfig.questions];
                } else {
                    let questions: QuestionConfig[] = [];
                    this.processConfig.questionsGroups.forEach((group) => {
                        questions = [...questions, ...group.questions];
                        questions = this._questionAttributePipe.transform(questions, response.data.user.attributes);
                    });
                    this.allQuestions = [...this.allQuestions, ...questions];
                }
                this.status = response.data.status;
                this.user = response.data.user;
                this.canSeePreviousEvaluations = response.data.seePreviousEvaluation;
                this.hasTwoPlusTwoCoaching = response.data.hasTwoPlusTwoCoaching;
                this.meetingClosed = response.data.calibrationMeetingClosed;
                this.processConfig.questionsGroups = this.processConfig.questionsGroups.sort((a, b) => a.order - b.order);
                this.nineboxConfig = response.data.nineBoxConfig;

                if (this.meetingClosed) this.allowAutoSave = false;

                if (response.data.user && response.data.user.goals) {
                    this.processConfig.questionsGroups = this._utilService.addGoalsAsQuestions(response.data.user.goals, this.processConfig.questionsGroups);
                }

                const formGroup = this._createFormGroup(this.processConfig.questionsGroups, response.data.evaluation);

                if (this.processConfig.questionsGroups) {
                    this.processConfig.questionsGroups.forEach((group) => {
                        group.questions = this._utilService.groupQuestions(group.questions);
                        group.hasCalculateQuestions = this.hasCalculateType(group);
                    });
                }

                this.formGroup = formGroup;

                if (this.autosave) {
                    this.autosave.autoSaveAllowed$.subscribe((value: boolean) => {
                        this.allowAutoSave = value;
                    });
                }

                this.formGroup.valueChanges.pipe(startWith(this.formGroup.value), distinctUntilChanged(), pairwise()).subscribe(async ([previous, current]) => {
                    if (JSON.stringify(previous) === JSON.stringify(current)) return;
                    this.allowAutoSave = false;
                    const valuesToSet = await this._utilService.calculateRelatedQuestions(current, this.allQuestions, previous, this.goalsIndexes);
                    valuesToSet.forEach((result) => {
                        this.formGroup.get(result.field).setValue(result.value, { emitEvent: true, onlySelf: true });
                    });
                    this.allowAutoSave = true;
                });

                this.formGroup.valueChanges.pipe(distinctUntilChanged(), debounceTime(15000)).subscribe((evaluation) => {
                    if (this.allowAutoSave && !this.timerStart) {
                        this.timerStart = true;
                        this._autoSave();
                    }
                });

                loading.dismiss();
            },
            (error) => {
                loading.dismiss();
                this.notifyError(error);
            },
        );
    }

    private _autoSave() {
        this.timerStart = false;
        this.hasPendingChanges = this.autoSaving && true;

        if (!this.autoSaving && this.status === FinalEvaluationStatus.NotSent) {
            this._saveEvaluation(this.formGroup, false);
        }
    }

    private _createFormGroup(groups: Array<QuestionGroup>, editingEvaluation: ManagerEvaluationRecord): UntypedFormGroup {
        const formGroup = new UntypedFormGroup({
            cycleId: new UntypedFormControl(this._cycleId),
            subjectUserId: new UntypedFormControl(this._userId),
        });

        this.goalsIndexes = this._utilService.getGoalsFieldsIndexes(groups);
        let currentIndex = 1;

        if (groups) {
            const displayedGroups = this._questionGroupsAttributePipe.transform(groups, this.user.attributes);

            groups.forEach((group) => {
                group.questions.forEach((question) => {
                    const controlName = "field" + currentIndex;
                    const validators =
                        displayedGroups.some((g) => g.id === group.id) && (question.required || question.goal) && question.hideQuestion !== true ? [Validators.required] : [];

                    formGroup.addControl(
                        controlName,
                        new UntypedFormControl(
                            {
                                value: this._utilService.getEvaluationValue(question, editingEvaluation, this.goalsIndexes, currentIndex),
                                disabled: this.status !== FinalEvaluationStatus.NotSent && !this.calibration,
                            },
                            validators,
                        ),
                    );

                    if (this.nineboxConfig) {
                        this.nineboxXOrder = this.nineboxConfig.xAxis.questionConfigId == question.id ? currentIndex : this.nineboxXOrder;
                        this.nineboxYOrder = this.nineboxConfig.yAxis.questionConfigId == question.id ? currentIndex : this.nineboxYOrder;
                    }

                    question.currentIndex = currentIndex;
                    currentIndex++;
                });
            });
        }

        return formGroup;
    }

    private _setReturnPath(storageKey: string): void {
        localStorage.setItem(storageKey, "manager-evaluation/" + this._cycleId + "/" + this._userId);
    }

    getAtualUserId() {
        var atualUserId = localStorage.getItem("ImpersonatedUser");
        var returnAtualUserId: string = "";
        if (atualUserId && atualUserId !== "") {
            returnAtualUserId = JSON.parse(atualUserId).userId;
        } else {
            atualUserId = localStorage.getItem("auth_data");
            if (atualUserId) {
                if (atualUserId) returnAtualUserId = JSON.parse(atualUserId).user_id;
            }
        }
        return returnAtualUserId;
    }

    private hasCalculateType(group: QuestionGroup) {
        return group.questions.some((q) => q.typeConfig.type === QuestionTypeEnum.Calculate);
    }

    private _hasAiReviewAccess() {
        this._evaluationService.getHasReviewAccess(this._authService.getLoggedUserId()).subscribe(
            (response) => {
                this.hasAiReviewAttribute = response.data;
            },
            (error) => this.notifyError(error),
        );
    }

    public async viewPreviousEvaluations() {
        const modal = await this._modalController.create({
            component: PreviousManagerEvaluationsModal,
            cssClass: "previous-manager-evaluation-modal",
            componentProps: {
                cycleId: this._cycleId,
                userId: this._userId,
                userName: this.user.name,
            },
        });

        return await modal.present();
    }

    @HostListener("window:beforeunload")
    ngOnDestroy() {
        localStorage.removeItem("UserOverview_ReturnPath");
    }
}
